import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError, invitationsApi, type InvitationAcceptResponseDTO } from '@/shared/api'
import { Icon } from '@/shared/ui/Icon'
import { LegalLinks } from '@/shared/ui/LegalLinks'
import { useAuth } from './AuthProvider'

interface Props {
  token: string
  /** Called when the user wants to leave the invite flow and go to login. */
  onDone?: () => void
}

function errorDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

/**
 * Pre-auth invitation acceptance screen.
 *
 * Behaviour depends on backend response:
 *   - The user is already logged in with the invite's email → the membership
 *     is added silently and we redirect to the chat shell.
 *   - The user does NOT exist → we collect ``full_name`` + ``password`` and
 *     create the account in the same call.
 *   - The user exists but is not authenticated → the backend marks the
 *     invite as accepted and asks us to send them to the login screen.
 *
 * On success we ALWAYS land on the login screen because the invite flow
 * deliberately does NOT open a session (no cookie minted) — the user has
 * to authenticate explicitly to bind the new membership to a real session.
 */
export function InviteAcceptScreen({ token, onDone }: Props) {
  const { status, refresh } = useAuth()

  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<InvitationAcceptResponseDTO | null>(null)
  const [requiresAccount, setRequiresAccount] = useState(false)

  // First attempt: no extra payload. Lets the backend tell us if the user
  // exists or not.
  const tryAccept = useCallback(
    async (extra?: { full_name?: string; password?: string }) => {
      setSubmitting(true)
      setError(null)
      try {
        const response = await invitationsApi.accept({
          token,
          full_name: extra?.full_name,
          password: extra?.password,
        })
        setResult(response)
        if (status === 'authenticated') {
          await refresh()
        }
      } catch (err) {
        const detail = errorDetail(err, 'Falha ao processar convite.')
        // If backend asks for a new account, switch the form into account mode.
        if (
          err instanceof ApiError &&
          err.detail?.toLowerCase().includes('full_name')
        ) {
          setRequiresAccount(true)
          setError(null)
        } else {
          setError(detail)
        }
      } finally {
        setSubmitting(false)
      }
    },
    [token, status, refresh],
  )

  useEffect(() => {
    if (!token || result) return
    void tryAccept()
  }, [token, result, tryAccept])

  const onSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      if (submitting) return
      if (!fullName.trim() || password.length < 8) {
        setError('Informe o nome completo e uma senha com 8+ caracteres.')
        return
      }
      await tryAccept({ full_name: fullName.trim(), password })
    },
    [fullName, password, submitting, tryAccept],
  )

  const inner = useMemo(() => {
    if (result) {
      return (
        <div className="invite-success">
          <h2>Convite aceito!</h2>
          <p>
            Você agora faz parte de <strong>{result.organization.display_name}</strong>{' '}
            como <strong>{result.role}</strong>.
          </p>
          {result.requires_login ? (
            <p className="invite-hint">
              Faça login com seu email <code>{result.user.email}</code> para
              começar a usar o workspace.
            </p>
          ) : (
            <p className="invite-hint">Sua sessão atual já tem acesso.</p>
          )}
          <button type="button" className="settings-button is-primary" onClick={onDone}>
            Ir para o login
          </button>
        </div>
      )
    }

    if (requiresAccount) {
      return (
        <form className="invite-form" onSubmit={(e) => void onSubmit(e)}>
          <h2>Criar conta para aceitar o convite</h2>
          <p>
            Esse convite ainda não tem uma conta associada. Defina seu nome e
            senha para entrar.
          </p>

          <label className="settings-field">
            <span className="settings-field-label">Nome completo</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="settings-input"
            />
          </label>
          <label className="settings-field">
            <span className="settings-field-label">Senha (8+ caracteres)</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
              className="settings-input"
            />
          </label>

          {error ? (
            <div className="settings-alert is-error" role="alert">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            className="settings-button is-primary"
            disabled={submitting}
          >
            {submitting ? 'Criando…' : 'Aceitar convite'}
          </button>
        </form>
      )
    }

    if (error) {
      return (
        <div className="invite-error">
          <h2>Não foi possível aceitar o convite</h2>
          <p>{error}</p>
          <button type="button" className="settings-button" onClick={onDone}>
            Voltar
          </button>
        </div>
      )
    }

    return (
      <div className="invite-loading">
        <div
          className="h-7 w-7 animate-spin rounded-full border-2 border-white/10 border-t-violet"
          aria-hidden="true"
        />
        <p>Validando convite…</p>
      </div>
    )
  }, [
    error,
    fullName,
    onDone,
    onSubmit,
    password,
    requiresAccount,
    result,
    submitting,
  ])

  return (
    <div className="relative grid min-h-screen w-full place-items-center overflow-hidden bg-bg-page px-6 py-12">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[720px] max-w-full -translate-x-1/2 -translate-y-1/2"
        style={{
          background:
            'radial-gradient(closest-side, rgba(124,92,255,0.22), rgba(124,92,255,0.06) 55%, transparent 75%)',
          filter: 'blur(48px)',
        }}
      />

      <div
        className="relative z-10 mx-auto flex w-full max-w-[420px] flex-col gap-5 rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl"
        style={{
          boxShadow:
            '0 30px 80px -30px rgba(124,92,255,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex flex-col items-center gap-2 text-center">
          <div
            className="grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/[0.04] text-violet"
            aria-hidden="true"
          >
            <Icon name="sparkles" size={18} />
          </div>
          <h1 className="m-0 text-[22px] font-medium tracking-[-0.01em] text-text">
            Aceitar convite
          </h1>
        </div>
        {inner}

        <div className="border-t border-white/10 pt-4">
          <LegalLinks />
        </div>
      </div>
    </div>
  )
}
